/**
 * Audit API - Cryptographically signed chain of custody logs.
 */

import apiClient, { type RequestConfig } from './client';
import type { PaginatedResponse, PaginationParams } from './types';

const ENDPOINT = '/audit';

export interface AuditEntry {
  id: string;
  action: string;
  user_id: string;
  timestamp: string;
  details: Record<string, unknown>;
  entry_hash?: string;
  chain_verified?: boolean;
}

export interface VerificationResult {
  verified: boolean;
  entry_count: number;
  first_entry?: string;
  last_entry?: string;
  errors?: Array<{ entry_id?: string; expected_hash?: string; actual_hash?: string; message?: string }>;
}

export const auditApi = {
  /**
   * Get audit trail for a matter.
   */
  async list(
    params: PaginationParams & { matter_id: string; action?: string },
    config?: RequestConfig
  ): Promise<PaginatedResponse<AuditEntry>> {
    const { data } = await apiClient.get<PaginatedResponse<AuditEntry>>(ENDPOINT, {
      ...config,
      params: params as Record<string, any>,
    });
    return data;
  },

  /**
   * Verify the cryptographic chain of custody.
   */
  async verify(matterId: string, config?: RequestConfig): Promise<VerificationResult> {
    const { data } = await apiClient.get<VerificationResult>(`${ENDPOINT}/verify`, {
      ...config,
      params: { matter_id: matterId },
    });
    return data;
  },

  /**
   * Export the signed ledger for legal defensibility.
   */
  async export(matterId: string, config?: RequestConfig): Promise<any> {
    const { data } = await apiClient.get(`${ENDPOINT}/export`, {
      ...config,
      params: { matter_id: matterId },
    });
    return data;
  },

  /**
   * Export the audit trail as CSV for court filings.
   */
  async exportCsv(matterId: string): Promise<Blob> {
    return apiClient.getBlob(`${ENDPOINT}/export`, {
      params: {
        matter_id: matterId,
        format: 'csv',
        include_verification: false,
      },
    });
  },

  /**
   * Get all auditable action types.
   */
  async getActionTypes(config?: RequestConfig): Promise<{ actions: { code: string; description: string }[] }> {
    const { data } = await apiClient.get<{ actions: { code: string; description: string }[] }>(`${ENDPOINT}/actions`, config);
    return data;
  },
};

export default auditApi;
